import { Annotation, Column, Group } from '@studio/design-system/form';
import { Mediator } from '@studio/design-system/input';
import type { HtmlAttributes } from '@studio/design-system/props';
import { formatUri, parseUri } from '@studio/models/net';
import type { Settings } from '@studio/models/video';
import { Protocol, VideoDefaults } from '@studio/models/video';
import { isRtsp, RtspDefaults, toConfiguration, toUri } from '@studio/models/video/rtsp';
import type { Configuration } from '@studio/models/video/rtsp';
import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import type { T_Nullable } from '@tryiris-ai/js-types';
import { defaultsDeep } from 'lodash-es';
import type { ReactElement, SyntheticEvent } from 'react';
import { useState } from 'react';
// Mount <AutoConfiguredLabel> beside the Stream URL Group (Multi-Add
// Phase 2). The slice key is `video.host` — the canonical source-
// derived discriminator for the RTSP URL (URL is composed from
// host/port/path; host is the strongest signal). The Group already
// carries an `<Annotation.Required />`; we reconcile by passing a
// fragment containing both annotations.
import { AutoConfiguredLabel } from '../auto-configured-label.jsx';
import { useProducer, useStore } from '../store/hooks.js';

/** @internal */
const Inner = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const [tempUrl, setTempUrl] = useState<string>('');
  const currentUrl = useStore((µ) => {
    const { video } = µ.candidate;
    const { protocol, ...rest } = video ?? {};

    return formatUri(
      toUri({
        protocol: isRtsp(protocol) ? protocol : Protocol.RTSP,
        ...rest
      })
    );
  });
  // Read `candidateId` and the live `host` from the store so the
  // <AutoConfiguredLabel> mount can address the depot slice. The slice
  // key for the Stream URL is `video.host` (the canonical source-
  // derived discriminator — URL composition from host/port/path means
  // host is the strongest signal).
  //
  // `candidate.video` is the `Configuration` union (RTSP | NDI |
  // Local). `Inner` is only mounted when the protocol is in the
  // RTSP family (see <Settings>'s `Components` map), so the RTSP
  // narrow is safe at runtime. We narrow via `isRtsp(protocol)` —
  // matches the pattern used in the existing `currentUrl` selector
  // a few lines up. Local doesn't carry `host`, so we coerce to
  // `undefined` on the unreachable Local branch.
  const candidateId = useStore((µ) => µ.candidate.id);
  const currentVideoHost = useStore((µ) => {
    const { video } = µ.candidate;
    if (!video || !isRtsp(video.protocol)) return undefined;
    // After the narrow, `video` is the RTSP `Configuration` which
    // declares `host: string` (see `models/video/rtsp/types.ts`).
    return (video as Configuration).host;
  });

  const handleUrlCommit = useLatestFn((nextUrl: string, event: SyntheticEvent) => {
    if (nextUrl === currentUrl) return;
    const config = toConfiguration(parseUri(nextUrl));
    if (!config) {
      event.preventDefault();
      setTempUrl(currentUrl || '');
      return;
    }
    producer((draft) => {
      draft.candidate.video = config;
    });
  });

  return (
    <Column {...props}>
      {/* Stream URL Group carries TWO annotations — the existing
          `<Annotation.Required />` plus the new `<AutoConfiguredLabel />`.
          The design-system `annotation` slot only accepts ONE node,
          so we compose both via a React fragment. Per `stay-in-scope`
          (ai.knowledge/global): the Group component is unchanged;
          fragment composition stays at the consumer. */}
      <Group
        annotation={
          <>
            <Annotation.Required />
            <AutoConfiguredLabel
              deviceId={candidateId}
              fieldPath='video.host'
              value={currentVideoHost}
            />
          </>
        }
        label='Stream URL'
      >
        <Mediator onCommit={handleUrlCommit} value={tempUrl || currentUrl} />
      </Group>
    </Column>
  );
};

/** RTSP settings. */
export const Rtsp = (props: HtmlAttributes): T_Nullable<ReactElement> => {
  const [isPrimed, setPrimed] = useState(false);
  const producer = useProducer();

  // On mount, normalize the Visca/IP video settings.
  useMount(() => {
    queueMicrotask(() => {
      producer(({ candidate }) => {
        const { video } = candidate;

        // Normalize configuration.
        candidate.video = defaultsDeep(
          { ...video },
          RtspDefaults,
          VideoDefaults
        ) as Settings<Configuration>;
      });

      setPrimed(true);
    });
  });

  return isPrimed ? <Inner {...props} /> : null;
};
